import React, { useCallback, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { SectionContentDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { TrackerExtraInfoProps, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useBlockProfiles } from 'state-hooks/accounts/useBlockProfiles';
import { useSlide } from 'ui-hooks/useSlide';
import { getValidContentAndProfile, useRearrangedContentIds } from 'utils/sectionContentUtils';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import { SectionExclusiveContentItem } from 'app-components/home/sections/items/SectionExclusiveContentItem';

const HorizontalGap = 8;

type SectionExclusiveContentsProps = {
  section: SectionContentDto;
  contents: Record<number, ContentRelayDto>;
  profileLites: Record<number, ProfileLiteRelayDto>;
};

export const SectionExclusiveContents = ({
  section,
  contents,
  profileLites,
  trackerExtraInfo,
}: SectionExclusiveContentsProps & TrackerExtraInfoProps) => {
  const { contentIds } = section;
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const { sessionSeed } = useAuthState();
  const blockedProfileIds = useBlockProfiles();
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);

  const rearrangedContentIds = useRearrangedContentIds(
    contentIds,
    section.shufflePreviewItem,
    section.previewItemCount,
    sessionSeed,
  );

  const handleOnClickTracker = useCallback(
    (contentId: number, listItemIndex: number) => {
      tracker.track(TrackingEventType.SelectExclusiveContentItem, {
        sectionId: section.id,
        sectionTitle: section.title,
        contentId,
        listItemIndex,
        trackerExtraInfo,
      });
    },
    [section.id, section.title, tracker, trackerExtraInfo],
  );

  const renderItem = useCallback(
    (contentId: number, index: number) => {
      const validationResult = getValidContentAndProfile(contentId, contents, profileLites, blockedProfileIds);
      if (!validationResult) {
        return null;
      }

      const { content, profile } = validationResult;

      return (
        <React.Fragment key={`exclusive-${content.id}-${index}`}>
          <SectionExclusiveContentItem
            content={content}
            profile={profile}
            index={index}
            handleOnClickTracker={handleOnClickTracker}
          />
        </React.Fragment>
      );
    },
    [blockedProfileIds, contents, handleOnClickTracker, profileLites],
  );

  if (rearrangedContentIds.length === 0) {
    return null;
  }

  return (
    <div>
      <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
      <div className="relative">
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
        />
        <div
          className={twMerge(
            'flex flex-col flex-wrap content-start overflow-x-scroll overflow-y-hidden',
            'h-[125px] px-4 gap-x-2 pc:p-0 pc:mx-[15px] pc:overflow-x-hidden',
            '[&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]',
          )}
          ref={slideRef}
          onScroll={handleSlide}>
          {rearrangedContentIds.map((contentId, index) => renderItem(contentId, index))}
        </div>
      </div>
    </div>
  );
};

export default SectionExclusiveContents;
